// Google Analytics utility functions

export const GA_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

// Initialize Google Analytics
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    console.warn('Google Analytics Measurement ID not configured');
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
  });
};

// Track page views
export const trackPageView = (pagePath, pageTitle) => {
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not initialized');
    return;
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

// Track events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window.gtag !== 'function') {
    console.warn('Google Analytics not initialized');
    return;
  }

  window.gtag('event', eventName, {
    event_category: 'engagement',
    event_label: eventParams.label || '',
    value: eventParams.value || 1,
    ...eventParams
  });
};

// Track form submissions
export const trackFormSubmit = (formName, formData = {}) => {
  trackEvent('form_submit', {
    event_category: 'form',
    event_label: formName,
    form_name: formName,
    engagement_time_msec: 1000,
  });
  
  // Also send to backend for server-side tracking
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  fetch(`${BACKEND_URL}/api/analytics/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      event_type: 'form_submit',
      page: window.location.pathname,
      metadata: JSON.stringify({ form_name: formName })
    })
  }).catch(err => console.warn('Backend analytics tracking failed:', err));
};

// Track button clicks
export const trackButtonClick = (buttonName, buttonLocation = '') => {
  trackEvent('click', {
    event_category: 'button',
    event_label: buttonName,
    button_name: buttonName,
    button_location: buttonLocation || window.location.pathname,
  });
  
  // Also send to backend
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  fetch(`${BACKEND_URL}/api/analytics/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      event_type: 'button_click',
      page: window.location.pathname,
      metadata: JSON.stringify({ button_name: buttonName, button_location: buttonLocation })
    })
  }).catch(err => console.warn('Backend analytics tracking failed:', err));
};

// Track conversions
export const trackConversion = (conversionType, value = 0) => {
  trackEvent('conversion', {
    event_category: 'conversion',
    event_label: conversionType,
    value: value,
    currency: 'USD'
  });
  
  // Also send to backend
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  fetch(`${BACKEND_URL}/api/analytics/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      event_type: 'conversion',
      page: window.location.pathname,
      metadata: JSON.stringify({ conversion_type: conversionType, value: value })
    })
  }).catch(err => console.warn('Backend analytics tracking failed:', err));
};

// Track scroll depth
export const initScrollTracking = () => {
  let maxScroll = 0;
  let ticking = false;

  const trackScroll = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
    );

    if (scrollPercent > maxScroll && scrollPercent % 25 === 0 && scrollPercent <= 100) {
      maxScroll = scrollPercent;
      trackEvent('scroll', {
        event_category: 'engagement',
        event_label: `${scrollPercent}%`,
        scroll_depth: scrollPercent
      });
    }
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(trackScroll);
      ticking = true;
    }
  });
};

// Track time on page
export const initTimeTracking = () => {
  const startTime = Date.now();

  const trackTimeOnPage = () => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    if (timeOnPage > 10) {  // Only track if user spent more than 10s
      trackEvent('timing_complete', {
        event_category: 'engagement',
        event_label: 'time_on_page',
        value: timeOnPage
      });
    }
  };

  // Track time on page when user leaves
  window.addEventListener('beforeunload', trackTimeOnPage);
  
  // Also track after 30 seconds for users who stay longer
  setTimeout(() => {
    const timeOnPage = Math.round((Date.now() - startTime) / 1000);
    trackEvent('timing_complete', {
      event_category: 'engagement',
      event_label: 'time_on_page_30s',
      value: timeOnPage
    });
  }, 30000);
};